<template>
  <main class="auth-page">
    <section class="auth-page__story">
      <UPlusLogo class="auth-page__logo" inverse @click="goHome" />

      <div class="auth-page__status">
        <i />
        UPlus 账户入口
      </div>
      <h1>让专业策略，<br />替你交易。</h1>
      <p class="auth-page__intro">
        一个账户管理全部交易所、机器人与资金流向，收益与费用透明可查。
      </p>

      <div class="auth-page__metrics">
        <LiveMetricCard
          label="平台累计结算收益 (USDT)"
          :value="formattedRevenue"
          :caption="publicError ? '公开数据暂时不可用' : '实时更新'"
          live
        />
        <LiveMetricCard label="全网累计收益率" :value="formattedYield" unit="%" live />
        <LiveMetricCard label="运行中机器人" :value="formattedRunningRobotCount" unit="台" />
        <LiveMetricCard label="稳定运行天数" :value="formattedRunDays" unit="天" />
      </div>

      <ul class="auth-page__features">
        <li>AI 多策略自动交易，7×24 小时运行</li>
        <li>资金 / 燃料 / 点卡账户集中管理</li>
        <li>实时风控扫描与保证金健康度监控</li>
      </ul>

      <footer class="auth-page__footer">
        <span>银行级加密存储</span><i /> <span>资金不托管</span><i />
        <span>API 只读交易授权</span><i /> <span>© 2026 UPlus</span>
      </footer>
    </section>

    <section class="auth-page__form-side">
      <div class="auth-panel">
        <div class="auth-panel__card">
          <div v-if="mode !== 'forgot'" class="auth-panel__tabs">
            <button :class="{ active: mode === 'login' }" type="button" @click="switchMode('login')">
              登录
            </button>
            <button
              :class="{ active: mode === 'register' }"
              type="button"
              @click="switchMode('register')"
            >
              注册
            </button>
          </div>

          <form v-if="mode === 'login'" key="login-form" @submit.prevent="login">
            <h2>欢迎回来</h2>
            <p class="auth-panel__subtitle">登录你的 UPlus 账户，继续自动交易</p>

            <label class="auth-field">
              <span>邮箱 / 手机号</span>
              <span class="auth-field__control">
                <input
                  ref="loginAccountInput"
                  v-model.trim="loginForm.account"
                  autocomplete="username"
                  placeholder="请输入邮箱或手机号"
                />
                <button
                  v-if="loginForm.account"
                  class="auth-field__control-button"
                  type="button"
                  title="清空账号"
                  aria-label="清空账号"
                  @mousedown.prevent
                  @click.prevent.stop="clearAccount('login')"
                >
                  <i class="el-icon-close" aria-hidden="true" />
                </button>
              </span>
            </label>

            <label class="auth-field">
              <span>密码</span>
              <span class="auth-field__control">
                <input
                  v-model="loginForm.password"
                  :type="loginPasswordVisible ? 'text' : 'password'"
                  autocomplete="current-password"
                  placeholder="请输入密码"
                />
                <button
                  class="auth-field__control-button"
                  :class="{ active: loginPasswordVisible }"
                  type="button"
                  :title="loginPasswordVisible ? '隐藏密码' : '显示密码'"
                  :aria-label="loginPasswordVisible ? '隐藏密码' : '显示密码'"
                  :aria-pressed="String(loginPasswordVisible)"
                  @mousedown.prevent
                  @click.prevent.stop="loginPasswordVisible = !loginPasswordVisible"
                >
                  <PasswordVisibilityIcon :visible="loginPasswordVisible" />
                </button>
              </span>
            </label>

            <div class="auth-panel__forgot">
              <button type="button" @click="forgotPassword">忘记密码？</button>
            </div>

            <button class="auth-panel__submit" type="submit" :disabled="submitting">
              {{ submitting ? '登录中…' : '登录' }}
            </button>
            <p class="auth-panel__agreement">
              <span>登录即代表你同意</span>
              <a href="#" @click.prevent.stop="openLegal('terms')">《服务协议》</a>
              <span>与</span>
              <a href="#" @click.prevent.stop="openLegal('privacy')">《隐私政策》</a>
            </p>
          </form>

          <form v-else-if="mode === 'register'" key="register-form" @submit.prevent="register">
            <h2>创建账户</h2>
            <p class="auth-panel__subtitle">三分钟完成注册，开启自动交易</p>

            <label class="auth-field">
              <span>邮箱</span>
              <span
                class="auth-field__control"
                :class="{ 'is-error': registerEmailError }"
              >
                <input
                  ref="registerAccountInput"
                  v-model.trim="registerForm.account"
                  type="email"
                  inputmode="email"
                  autocomplete="email"
                  placeholder="请输入邮箱"
                  @blur="registerAccountTouched = true"
                />
                <button
                  v-if="registerForm.account"
                  class="auth-field__control-button"
                  type="button"
                  title="清空账号"
                  aria-label="清空账号"
                  @mousedown.prevent
                  @click.prevent.stop="clearAccount('register')"
                >
                  <i class="el-icon-close" aria-hidden="true" />
                </button>
              </span>
              <span v-if="registerEmailError" class="auth-field__error">
                {{ registerEmailError }}
              </span>
            </label>

            <label class="auth-field">
              <span>验证码</span>
              <span class="auth-field__inline">
                <input
                  v-model.trim="registerForm.code"
                  type="text"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="6"
                  pattern="[0-9]*"
                  placeholder="6 位验证码"
                  @input="normalizeVerificationCode"
                />
                <button type="button" :disabled="codeSeconds > 0" @click="sendCode">
                  {{ codeSeconds > 0 ? `${codeSeconds}s` : '获取验证码' }}
                </button>
              </span>
            </label>

            <label class="auth-field">
              <span>设置密码</span>
              <span class="auth-field__control">
                <input
                  v-model="registerForm.password"
                  :type="registerPasswordVisible ? 'text' : 'password'"
                  autocomplete="new-password"
                  placeholder="至少 8 位，含大小写字母、数字和符号"
                />
                <button
                  class="auth-field__control-button"
                  :class="{ active: registerPasswordVisible }"
                  type="button"
                  :title="registerPasswordVisible ? '隐藏密码' : '显示密码'"
                  :aria-label="registerPasswordVisible ? '隐藏密码' : '显示密码'"
                  :aria-pressed="String(registerPasswordVisible)"
                  @mousedown.prevent
                  @click.prevent.stop="registerPasswordVisible = !registerPasswordVisible"
                >
                  <PasswordVisibilityIcon :visible="registerPasswordVisible" />
                </button>
              </span>
            </label>

            <label class="auth-field">
              <span>邀请码（选填）</span>
              <input v-model.trim="registerForm.invitation" placeholder="如有邀请码请填写" />
            </label>

            <label class="auth-panel__check">
              <input v-model="registerForm.agreed" type="checkbox" />
              <span class="auth-panel__check-copy">
                <span>我已阅读并同意</span>
                <a href="#" @click.prevent.stop="openLegal('terms')">《服务协议》</a>
                <span>与</span>
                <a href="#" @click.prevent.stop="openLegal('privacy')">《隐私政策》</a>
              </span>
            </label>

            <button class="auth-panel__submit" type="submit" :disabled="submitting">
              {{ submitting ? '注册中…' : '注册' }}
            </button>
          </form>

          <form
            v-else
            key="forgot-password-form"
            class="forgot-password"
            @submit.prevent.stop="handleForgotSubmit"
          >
            <button
              class="forgot-password__back"
              type="button"
              @click.prevent.stop="backToLogin"
            >
              <i class="el-icon-arrow-left" aria-hidden="true" />
              返回登录
            </button>

            <h2>重置密码</h2>
            <p class="auth-panel__subtitle forgot-password__subtitle">
              {{ forgotStep === 1
                ? '输入注册邮箱，我们将发送验证码用于身份验证'
                : '验证码已发送，请填写验证码并设置新密码' }}
            </p>

            <div class="forgot-password__steps" aria-label="重置密码进度">
              <div class="forgot-password__step is-active" :class="{ 'is-complete': forgotStep === 2 }">
                <span>{{ forgotStep === 2 ? '✓' : '1' }}</span>
                <strong>验证邮箱</strong>
              </div>
              <i :class="{ 'is-active': forgotStep === 2 }" />
              <div class="forgot-password__step" :class="{ 'is-active': forgotStep === 2 }">
                <span>2</span>
                <strong>设置新密码</strong>
              </div>
            </div>

            <template v-if="forgotStep === 1">
              <label class="auth-field forgot-password__email-field">
                <span>注册邮箱</span>
                <span class="auth-field__control" :class="{ 'is-error': forgotEmailError }">
                  <input
                    ref="forgotEmailInput"
                    v-model.trim="forgotForm.email"
                    type="email"
                    inputmode="email"
                    autocomplete="email"
                    placeholder="请输入注册时使用的邮箱"
                    @blur="forgotEmailTouched = true"
                  />
                  <button
                    v-if="forgotForm.email"
                    class="auth-field__control-button"
                    type="button"
                    title="清空邮箱"
                    aria-label="清空邮箱"
                    @mousedown.prevent
                    @click.prevent.stop="clearForgotEmail"
                  >
                    <i class="el-icon-close" aria-hidden="true" />
                  </button>
                </span>
                <span v-if="forgotEmailError" class="auth-field__error">{{ forgotEmailError }}</span>
                <small class="forgot-password__hint">
                  我们会向该邮箱发送一封含 6 位验证码的邮件，请注意查收垃圾邮件箱。
                </small>
              </label>

              <button
                class="auth-panel__submit"
                type="submit"
                :disabled="forgotCodeSending"
              >
                {{ forgotCodeSending ? '发送中…' : '发送验证码' }}
              </button>
            </template>

            <template v-else>
              <div class="forgot-password__email-summary">
                <span><i class="el-icon-message" aria-hidden="true" />{{ forgotForm.email }}</span>
                <button type="button" @click="changeForgotEmail">更换</button>
              </div>

              <label class="auth-field">
                <span>邮箱验证码</span>
                <span class="auth-field__inline">
                  <input
                    v-model="forgotForm.code"
                    type="text"
                    inputmode="numeric"
                    autocomplete="one-time-code"
                    maxlength="6"
                    pattern="[0-9]*"
                    placeholder="6 位验证码"
                    @input="normalizeForgotCode"
                  />
                  <button
                    type="button"
                    :disabled="forgotCodeSeconds > 0 || forgotCodeSending"
                    @click="sendForgotCode"
                  >
                    {{ forgotCodeSeconds > 0 ? `${forgotCodeSeconds} 秒后重发` : '重新发送' }}
                  </button>
                </span>
              </label>

              <label class="auth-field forgot-password__password-field">
                <span>新密码</span>
                <span class="auth-field__control">
                  <input
                    v-model="forgotForm.password"
                    :type="forgotPasswordVisible ? 'text' : 'password'"
                    autocomplete="new-password"
                    placeholder="至少 8 位，含大小写字母、数字和符号"
                  />
                  <button
                    class="auth-field__control-button"
                    :class="{ active: forgotPasswordVisible }"
                    type="button"
                    :title="forgotPasswordVisible ? '隐藏密码' : '显示密码'"
                    :aria-label="forgotPasswordVisible ? '隐藏密码' : '显示密码'"
                    :aria-pressed="String(forgotPasswordVisible)"
                    @mousedown.prevent
                    @click.prevent.stop="forgotPasswordVisible = !forgotPasswordVisible"
                  >
                    <PasswordVisibilityIcon :visible="forgotPasswordVisible" />
                  </button>
                </span>
                <span
                  v-if="forgotForm.password"
                  class="forgot-password__strength"
                  :class="`is-level-${forgotStrengthLevel}`"
                >
                  <i v-for="index in 3" :key="index" :class="{ active: forgotStrengthLevel >= index }" />
                  <em>{{ forgotStrengthText }}</em>
                </span>
              </label>

              <label class="auth-field forgot-password__confirm-field">
                <span>确认新密码</span>
                <span
                  class="auth-field__control"
                  :class="{ 'is-error': forgotPasswordMismatch }"
                >
                  <input
                    v-model="forgotForm.confirmPassword"
                    :type="forgotConfirmPasswordVisible ? 'text' : 'password'"
                    autocomplete="new-password"
                    placeholder="请再次输入新密码"
                  />
                  <button
                    class="auth-field__control-button"
                    :class="{ active: forgotConfirmPasswordVisible }"
                    type="button"
                    :title="forgotConfirmPasswordVisible ? '隐藏密码' : '显示密码'"
                    :aria-label="forgotConfirmPasswordVisible ? '隐藏密码' : '显示密码'"
                    :aria-pressed="String(forgotConfirmPasswordVisible)"
                    @mousedown.prevent
                    @click.prevent.stop="forgotConfirmPasswordVisible = !forgotConfirmPasswordVisible"
                  >
                    <PasswordVisibilityIcon :visible="forgotConfirmPasswordVisible" />
                  </button>
                </span>
                <span v-if="forgotPasswordMismatch" class="auth-field__error">
                  两次输入的密码不一致
                </span>
              </label>

              <button class="auth-panel__submit" type="submit" :disabled="submitting">
                {{ submitting ? '重置中…' : '完成重置' }}
              </button>
              <p class="forgot-password__security-tip">
                重置成功后，所有已登录设备将被强制退出；交易所 API 绑定不受影响。
              </p>
            </template>
          </form>
        </div>

        <p class="auth-panel__support">
          遇到问题？
          <button type="button" @click="openCustomerService">
            联系在线客服
          </button>
        </p>
      </div>
    </section>

    <ToastMessage :visible="toast.visible" :message="toast.message" :type="toast.type" />
  </main>
</template>

<script>
import UPlusLogo from '@/components/rebuild/UPlusLogo.vue'
import LiveMetricCard from '@/components/rebuild/LiveMetricCard.vue'
import PasswordVisibilityIcon from '@/components/rebuild/PasswordVisibilityIcon.vue'
import ToastMessage from '@/components/rebuild/ToastMessage.vue'
import {
  getAuth,
  getAuthCode,
  sendForgotPasswordCode,
  submitForgotPassword,
  toRegister
} from '@/server/auth'
import { getUserInfo } from '@/server/rebate'
import { loadPublicSnapshot } from '@/api/portal'
import { finiteNumber, formatNumber, formatRate } from '@/utils/portal-data'
import { navigateFromLogo } from '@/utils/logo-navigation'

function accountType(account) {
  return account.indexOf('@') > -1 ? 1 : 2
}

const REMEMBERED_ACCOUNT_KEY = 'uplusRememberedAccount'
const STRONG_PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d\s])\S{8,}$/
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

function loadRememberedAccount() {
  try {
    return window.localStorage.getItem(REMEMBERED_ACCOUNT_KEY) || ''
  } catch (error) {
    return ''
  }
}

export default {
  name: 'AuthPage',
  components: { UPlusLogo, LiveMetricCard, PasswordVisibilityIcon, ToastMessage },
  props: {
    initialMode: { type: String, default: 'login' }
  },
  data() {
    const forgotMode = this.$route.query.forgot === '1'
    const rememberedAccount = loadRememberedAccount()
    return {
      mode: forgotMode ? 'forgot' : this.initialMode,
      platform: null,
      publicError: '',
      submitting: false,
      loginPasswordVisible: false,
      registerPasswordVisible: false,
      forgotPasswordVisible: false,
      forgotConfirmPasswordVisible: false,
      registerAccountTouched: false,
      forgotEmailTouched: false,
      forgotStep: 1,
      codeSeconds: 0,
      codeTimer: null,
      forgotCodeSeconds: 0,
      forgotCodeTimer: null,
      forgotCodeSending: false,
      toastTimer: null,
      toast: { visible: false, message: '', type: 'info' },
      loginForm: {
        account: rememberedAccount,
        password: ''
      },
      registerForm: {
        account: '',
        code: '',
        password: '',
        invitation: '',
        agreed: false
      },
      forgotForm: {
        email: EMAIL_PATTERN.test(rememberedAccount) ? rememberedAccount : '',
        code: '',
        password: '',
        confirmPassword: ''
      }
    }
  },
  computed: {
    registerEmailError() {
      const email = this.registerForm.account
      if (!email) return this.registerAccountTouched ? '请输入邮箱' : ''
      return EMAIL_PATTERN.test(email) ? '' : '邮箱格式不正确'
    },
    forgotEmailError() {
      const email = this.forgotForm.email
      if (!email) return this.forgotEmailTouched ? '请输入注册邮箱' : ''
      return EMAIL_PATTERN.test(email) ? '' : '邮箱格式不正确，请检查后重试'
    },
    forgotPasswordMismatch() {
      return Boolean(
        this.forgotForm.confirmPassword &&
        this.forgotForm.confirmPassword !== this.forgotForm.password
      )
    },
    forgotStrengthLevel() {
      const password = this.forgotForm.password
      if (!password) return 0
      let score = 0
      if (password.length >= 8) score += 1
      if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1
      if (/\d/.test(password)) score += 1
      if (/[^A-Za-z\d\s]/.test(password)) score += 1
      if (score <= 1) return 1
      if (score <= 3) return 2
      return 3
    },
    forgotStrengthText() {
      return ['弱', '弱', '中', '强'][this.forgotStrengthLevel]
    },
    formattedRevenue() {
      return formatNumber(finiteNumber(this.platform && this.platform.profit))
    },
    formattedYield() {
      const value = finiteNumber(this.platform && this.platform.profit_yield)
      return value === null ? '--' : formatRate(value).replace('%', '')
    },
    formattedRunningRobotCount() {
      const value = finiteNumber(this.platform && this.platform.running_robot_count)
      return value === null ? '--' : formatNumber(value, 0)
    },
    formattedRunDays() {
      const value = finiteNumber(this.platform && this.platform.run_days)
      return value === null ? '--' : formatNumber(value, 0)
    }
  },
  watch: {
    initialMode(value) {
      if (this.$route.query.forgot !== '1') this.mode = value
    },
    '$route.query.forgot'(value) {
      if (value === '1') {
        this.activateForgotMode()
      } else if (this.mode === 'forgot') {
        this.mode = this.initialMode
      }
    }
  },
  mounted() {
    this.fetchPublicData()
  },
  beforeDestroy() {
    window.clearInterval(this.codeTimer)
    window.clearInterval(this.forgotCodeTimer)
    window.clearTimeout(this.toastTimer)
  },
  methods: {
    goHome() {
      if (['/login', '/register'].includes(this.$route.path)) {
        if (window.history.length > 1) {
          this.$router.go(-1)
        } else {
          this.$router.replace('/')
        }
        return
      }
      navigateFromLogo(this.$router, '/')
    },
    openCustomerService() {
      this.$root.$emit('open-customer-service')
    },
    openLegal(tab) {
      this.$root.$emit('open-legal-terms', tab)
    },
    async fetchPublicData() {
      const snapshot = await loadPublicSnapshot()
      this.platform = snapshot.platform
      this.publicError = snapshot.platform ? '' : '公开数据暂时不可用'
    },
    switchMode(mode) {
      this.mode = mode
      this.loginPasswordVisible = false
      this.registerPasswordVisible = false
      this.forgotPasswordVisible = false
      this.forgotConfirmPasswordVisible = false
      this.registerAccountTouched = false
      window.clearInterval(this.forgotCodeTimer)
      this.forgotCodeSeconds = 0
      const path = mode === 'login' ? '/login' : '/register'
      if (this.$route.path !== path) this.$router.replace(path)
      else if (this.$route.query.forgot) this.$router.replace(path)
    },
    notify(message, type = 'info') {
      window.clearTimeout(this.toastTimer)
      this.toast = { visible: true, message, type }
      this.toastTimer = window.setTimeout(() => {
        this.toast.visible = false
      }, 2600)
    },
    validateAccount(account) {
      if (!account) {
        this.notify('请输入邮箱或手机号', 'error')
        return false
      }
      return true
    },
    clearAccount(mode) {
      const form = mode === 'register' ? this.registerForm : this.loginForm
      const refName = mode === 'register' ? 'registerAccountInput' : 'loginAccountInput'
      form.account = ''
      if (mode === 'register') this.registerAccountTouched = false
      this.$nextTick(() => {
        const input = this.$refs[refName]
        if (input && typeof input.focus === 'function') input.focus()
      })
    },
    normalizeVerificationCode(event) {
      const value = event && event.target ? event.target.value : this.registerForm.code
      this.registerForm.code = String(value || '')
        .replace(/\D/g, '')
        .slice(0, 6)
    },
    normalizeForgotCode(event) {
      const value = event && event.target ? event.target.value : this.forgotForm.code
      this.forgotForm.code = String(value || '')
        .replace(/\D/g, '')
        .slice(0, 6)
    },
    validateRegisterEmail() {
      this.registerAccountTouched = true
      return !this.registerEmailError
    },
    async login() {
      if (!this.validateAccount(this.loginForm.account)) return
      if (!this.loginForm.password) {
        this.notify('请输入密码', 'error')
        return
      }

      this.submitting = true
      try {
        const result = await getAuth(
          this.loginForm.account,
          this.loginForm.password,
          accountType(this.loginForm.account)
        )
        if (!result || result.success === false || !result.data || !result.data.token) {
          throw new Error((result && result.message) || '登录失败')
        }
        window.localStorage.setItem('token', result.data.token)
        window.localStorage.setItem('loginTime', String(Date.now()))
        window.localStorage.setItem(REMEMBERED_ACCOUNT_KEY, this.loginForm.account)
        try {
          const userResult = await getUserInfo()
          if (userResult && userResult.success && userResult.data) {
            window.localStorage.setItem('userInfo', JSON.stringify(userResult.data))
          }
        } catch (error) {
          // Token 已写入，用户资料稍后在用户端重试。
        }
        this.notify('登录成功，正在进入总览…', 'success')
        const redirect = this.$route.query.redirect
        window.setTimeout(() => this.$router.replace(redirect || '/app/overview'), 450)
      } catch (error) {
        this.notify(error.message || '登录失败，请稍后重试', 'error')
      } finally {
        this.submitting = false
      }
    },
    async sendCode() {
      if (!this.validateRegisterEmail()) return
      try {
        const result = await getAuthCode(1, 1, this.registerForm.account)
        if (result && result.success === false) throw new Error(result.message || '验证码发送失败')
        this.notify('验证码已发送，10 分钟内有效', 'success')
        this.codeSeconds = 60
        window.clearInterval(this.codeTimer)
        this.codeTimer = window.setInterval(() => {
          this.codeSeconds -= 1
          if (this.codeSeconds <= 0) window.clearInterval(this.codeTimer)
        }, 1000)
      } catch (error) {
        this.notify(error.message || '验证码发送失败', 'error')
      }
    },
    async register() {
      const form = this.registerForm
      if (!this.validateRegisterEmail()) return
      if (!form.code) return this.notify('请输入验证码', 'error')
      if (!/^\d{6}$/.test(form.code)) return this.notify('请输入 6 位数字验证码', 'error')
      if (!form.password) return this.notify('请输入密码', 'error')
      if (!STRONG_PASSWORD_PATTERN.test(form.password)) {
        return this.notify('密码需至少 8 位，包含大小写字母、数字和特殊字符', 'error')
      }
      if (!form.agreed) return this.notify('请先阅读并同意服务协议', 'error')

      this.submitting = true
      try {
        const result = await toRegister(
          form.account,
          form.password,
          1,
          '',
          form.invitation,
          form.code
        )
        if (!result || result.success === false) {
          throw new Error((result && result.message) || '注册失败')
        }
        this.notify('注册成功，请登录你的账户', 'success')
        window.setTimeout(() => this.switchMode('login'), 450)
      } catch (error) {
        this.notify(error.message || '注册失败，请稍后重试', 'error')
      } finally {
        this.submitting = false
      }
    },
    forgotPassword() {
      this.activateForgotMode()
      if (this.$route.query.forgot !== '1') {
        this.$router.replace({ path: '/login', query: { ...this.$route.query, forgot: '1' } })
      }
    },
    activateForgotMode() {
      window.clearInterval(this.forgotCodeTimer)
      const loginAccount = this.loginForm.account.trim()
      this.mode = 'forgot'
      this.forgotStep = 1
      this.forgotCodeSeconds = 0
      this.forgotEmailTouched = false
      this.forgotPasswordVisible = false
      this.forgotConfirmPasswordVisible = false
      this.forgotForm = {
        email: EMAIL_PATTERN.test(loginAccount) ? loginAccount : this.forgotForm.email,
        code: '',
        password: '',
        confirmPassword: ''
      }
      this.$nextTick(() => {
        const input = this.$refs.forgotEmailInput
        if (input && typeof input.focus === 'function') input.focus()
      })
    },
    backToLogin() {
      window.clearInterval(this.forgotCodeTimer)
      window.clearTimeout(this.toastTimer)
      this.forgotCodeSeconds = 0
      this.toast.visible = false
      this.mode = 'login'
      const query = { ...this.$route.query }
      delete query.forgot
      this.$router.replace({ path: '/login', query })
    },
    clearForgotEmail() {
      this.forgotForm.email = ''
      this.forgotEmailTouched = false
      this.$nextTick(() => {
        const input = this.$refs.forgotEmailInput
        if (input && typeof input.focus === 'function') input.focus()
      })
    },
    changeForgotEmail() {
      window.clearInterval(this.forgotCodeTimer)
      this.forgotStep = 1
      this.forgotCodeSeconds = 0
      this.forgotForm.code = ''
      this.forgotForm.password = ''
      this.forgotForm.confirmPassword = ''
      this.forgotPasswordVisible = false
      this.forgotConfirmPasswordVisible = false
      this.$nextTick(() => {
        const input = this.$refs.forgotEmailInput
        if (input && typeof input.focus === 'function') input.focus()
      })
    },
    validateForgotEmail() {
      this.forgotEmailTouched = true
      return !this.forgotEmailError
    },
    startForgotCountdown() {
      window.clearInterval(this.forgotCodeTimer)
      this.forgotCodeSeconds = 60
      this.forgotCodeTimer = window.setInterval(() => {
        this.forgotCodeSeconds -= 1
        if (this.forgotCodeSeconds <= 0) {
          this.forgotCodeSeconds = 0
          window.clearInterval(this.forgotCodeTimer)
        }
      }, 1000)
    },
    async sendForgotCode() {
      if (this.forgotCodeSeconds > 0 || this.forgotCodeSending) return
      if (!this.validateForgotEmail()) return

      this.forgotCodeSending = true
      try {
        const result = await sendForgotPasswordCode(this.forgotForm.email)
        if (!result || result.success === false) {
          throw new Error((result && result.message) || '验证码发送失败')
        }
        this.forgotStep = 2
        this.startForgotCountdown()
        this.notify(`验证码已发送至 ${this.forgotForm.email}，10 分钟内有效`, 'success')
      } catch (error) {
        this.notify(error.message || '验证码发送失败，请稍后重试', 'error')
      } finally {
        this.forgotCodeSending = false
      }
    },
    handleForgotSubmit() {
      if (this.forgotStep === 1) return this.sendForgotCode()
      return this.resetForgotPassword()
    },
    async resetForgotPassword() {
      const form = this.forgotForm
      if (!/^\d{6}$/.test(form.code)) return this.notify('请输入 6 位邮箱验证码', 'error')
      if (!STRONG_PASSWORD_PATTERN.test(form.password)) {
        return this.notify('新密码需至少 8 位，包含大小写字母、数字和特殊字符', 'error')
      }
      if (form.confirmPassword !== form.password) {
        return this.notify('两次输入的密码不一致', 'error')
      }

      this.submitting = true
      try {
        const resetResult = await submitForgotPassword(form.email, form.password, form.code)
        if (!resetResult || resetResult.success === false) {
          throw new Error((resetResult && resetResult.message) || '密码重置失败')
        }
        window.localStorage.removeItem('token')
        window.localStorage.removeItem('userInfo')
        window.sessionStorage.removeItem('uplusStrategyDetail')
        window.localStorage.setItem(REMEMBERED_ACCOUNT_KEY, form.email)
        this.loginForm.account = form.email
        this.loginForm.password = ''
        this.backToLogin()
        this.notify('密码重置成功，请使用新密码登录', 'success')
      } catch (error) {
        this.notify(error.message || '密码重置失败，请稍后重试', 'error')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.auth-page {
  display: grid;
  min-height: 100vh;
  grid-template-columns: 1.1fr 1fr;
  background: #f5f6f9;
  animation: up-fade-in 0.2s ease;
}

.auth-page__story {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 56px 64px;
  color: #fff;
  background: radial-gradient(900px 420px at 88% -30%, rgba(74, 140, 255, 0.4), transparent 62%),
    radial-gradient(700px 340px at -8% 130%, rgba(31, 107, 255, 0.35), transparent 60%),
    linear-gradient(130deg, #081737, #123289 58%, #1d5bd8);
}

.auth-page__logo {
  align-self: flex-start;
  color: #fff;
}

.auth-page__status {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 8px;
  margin-top: 64px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  font-size: 12.5px;
  font-weight: 700;
}

.auth-page__status i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #9fe8c3;
  animation: up-pulse 2s ease infinite;
}

.auth-page h1 {
  margin: 18px 0 0;
  font-size: 40px;
  line-height: 1.35;
  letter-spacing: 0.5px;
}

.auth-page__intro {
  max-width: 440px;
  margin: 14px 0 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 14.5px;
  line-height: 1.8;
}

.auth-page__metrics {
  display: grid;
  max-width: 520px;
  grid-template-columns: repeat(2, minmax(180px, 1fr));
  gap: 14px;
  margin-top: 36px;
}

.auth-page__features {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 32px 0 0;
  padding: 0;
  list-style: none;
  font-size: 14px;
  font-weight: 600;
}

.auth-page__features li::before {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-right: 11px;
  content: "";
  border-radius: 50%;
  background: #9fe8c3;
}

.auth-page__footer {
  display: flex;
  margin-top: auto;
  padding-top: 32px;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
}

.auth-page__footer i {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
}

.auth-page__form-side {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  overflow: auto;
  background: #f6f8fc;
}

.auth-panel {
  width: 440px;
  max-width: 94%;
}

.auth-panel__card {
  padding: 34px 36px;
  border: 1px solid var(--up-line);
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 6px rgba(16, 24, 40, 0.04), 0 18px 44px rgba(16, 24, 40, 0.07);
}

.auth-panel__tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 26px;
  padding: 4px;
  border-radius: 10px;
  background: #f1f3f8;
}

.auth-panel__tabs button {
  flex: 1;
  padding: 9px 0;
  color: #79808f;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 14px;
  font-weight: 800;
}

.auth-panel__tabs button.active {
  color: var(--up-blue);
  background: #fff;
  box-shadow: 0 2px 6px rgba(16, 24, 40, 0.08);
}

.auth-panel h2 {
  margin: 0;
  font-size: 24px;
}

.auth-panel__subtitle {
  margin: 7px 0 24px;
  color: #8a90a0;
  font-size: 13.5px;
}

.auth-field {
  display: block;
  margin-bottom: 14px;
}

.auth-field > span:first-child {
  display: block;
  margin-bottom: 7px;
  color: #5a6072;
  font-size: 13.5px;
  font-weight: 600;
}

.auth-field input {
  width: 100%;
  padding: 12px 14px;
  color: var(--up-ink);
  border: 1px solid #dfe3ec;
  border-radius: 9px;
  outline: none;
  background: #fafbfd;
  font-size: 14.5px;
}

.auth-field input:focus {
  border-color: var(--up-blue);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(31, 107, 255, 0.12);
}

.auth-field__control {
  position: relative;
  display: block;
}

.auth-field__control input {
  padding-right: 46px;
}

.auth-field__control.is-error input {
  border-color: #f04438;
  background: #fff;
}

.auth-field__control.is-error input:focus {
  border-color: #f04438;
  box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.12);
}

.auth-field > .auth-field__error {
  display: block;
  margin-top: 6px;
  margin-bottom: 0;
  color: #f04438;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.auth-field__control-button {
  position: absolute;
  top: 50%;
  right: 9px;
  display: inline-flex;
  width: 30px;
  height: 30px;
  padding: 0;
  align-items: center;
  justify-content: center;
  color: #98a0af;
  border: 0;
  border-radius: 7px;
  outline: none;
  background: transparent;
  font-size: 16px;
  transform: translateY(-50%);
  transition: color 0.16s ease, background-color 0.16s ease;
}

.auth-field__control-button:hover {
  color: #5a6072;
  background: #eef2f8;
}

.auth-field__control-button.active {
  color: var(--up-blue);
  background: #ebf1fe;
}

.auth-field__control-button:focus-visible {
  color: var(--up-blue);
  box-shadow: 0 0 0 2px rgba(31, 107, 255, 0.18);
}

.auth-field__inline {
  display: flex !important;
  gap: 10px;
}

.auth-field__inline input {
  min-width: 0;
  flex: 1;
}

.auth-field__inline button {
  padding: 0 16px;
  color: var(--up-blue);
  border: 0;
  border-radius: 9px;
  background: #ebf1fe;
  font-size: 13.5px;
  font-weight: 700;
  white-space: nowrap;
}

.auth-field__inline button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.auth-panel__forgot {
  display: flex;
  justify-content: flex-end;
  margin: -4px 0 14px;
}

.auth-panel__forgot button,
.auth-panel__support button {
  padding: 0;
  color: var(--up-blue);
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
}

.auth-panel__submit {
  width: 100%;
  padding: 13px 0;
  color: #fff;
  border: 0;
  border-radius: 9px;
  background: var(--up-blue);
  box-shadow: 0 10px 24px rgba(31, 107, 255, 0.28);
  font-size: 15px;
  font-weight: 800;
}

.auth-panel__submit:disabled {
  cursor: wait;
  opacity: 0.7;
}

.auth-panel__agreement {
  display: flex;
  margin: 18px 0 0;
  align-items: center;
  justify-content: center;
  column-gap: 4px;
  flex-wrap: wrap;
  color: #98a0af;
  font-size: 12.5px;
  text-align: center;
}

.auth-panel__agreement a,
.auth-panel__check a {
  font-weight: 700;
}

.auth-panel__check {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 2px 0 20px;
  color: #5a6072;
  font-size: 13px;
  line-height: 1.6;
}

.auth-panel__check-copy {
  display: inline-flex;
  align-items: center;
  column-gap: 4px;
  flex-wrap: wrap;
}

.auth-panel__check input {
  width: 18px;
  height: 18px;
  margin: 1px 0 0;
  accent-color: var(--up-blue);
}

.auth-panel__support {
  margin: 18px 0 0;
  color: #98a0af;
  font-size: 12.5px;
  text-align: center;
}

.forgot-password__back {
  display: inline-flex;
  margin: 0 0 16px;
  padding: 0;
  align-items: center;
  gap: 6px;
  color: #79808f;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  transition: color 0.16s ease;
}

.forgot-password__back:hover {
  color: var(--up-blue);
}

.forgot-password__subtitle {
  margin-bottom: 22px;
  line-height: 1.6;
}

.forgot-password__steps {
  display: flex;
  margin-bottom: 24px;
  align-items: center;
  gap: 10px;
}

.forgot-password__steps > i {
  min-width: 20px;
  height: 2px;
  flex: 1;
  border-radius: 2px;
  background: #eef0f5;
  transition: background-color 0.18s ease;
}

.forgot-password__steps > i.is-active {
  background: var(--up-blue);
}

.forgot-password__step {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #98a0af;
  white-space: nowrap;
}

.forgot-password__step > span {
  display: inline-flex;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
  align-items: center;
  justify-content: center;
  color: #98a0af;
  border-radius: 50%;
  background: #eef0f5;
  font-size: 12px;
  font-weight: 800;
}

.forgot-password__step strong {
  font-size: 12.5px;
  font-weight: 700;
}

.forgot-password__step.is-active {
  color: var(--up-ink);
}

.forgot-password__step.is-active > span {
  color: #fff;
  background: var(--up-blue);
}

.forgot-password__step.is-complete > span {
  color: #0e9f5c;
  background: #e4f5ec;
}

.forgot-password__email-field {
  margin-bottom: 16px;
}

.forgot-password__hint {
  display: block;
  margin-top: 7px;
  color: #98a0af;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
}

.forgot-password__email-summary {
  display: flex;
  margin-bottom: 14px;
  padding: 11px 13px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #3a3f4e;
  border: 1px solid #eef0f5;
  border-radius: 9px;
  background: #f7f8fb;
  font-size: 13px;
}

.forgot-password__email-summary > span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.forgot-password__email-summary i {
  flex-shrink: 0;
  color: #79808f;
  font-size: 15px;
}

.forgot-password__email-summary button {
  flex-shrink: 0;
  padding: 0;
  color: var(--up-blue);
  border: 0;
  background: transparent;
  font-size: 12.5px;
  font-weight: 800;
}

.forgot-password__password-field {
  margin-bottom: 12px;
}

.forgot-password__confirm-field {
  margin-bottom: 18px;
}

.forgot-password__strength {
  display: flex !important;
  margin-top: 9px;
  align-items: center;
  gap: 8px;
}

.forgot-password__strength i {
  height: 4px;
  flex: 1;
  border-radius: 99px;
  background: #eef0f5;
}

.forgot-password__strength.is-level-1 i.active {
  background: #e5484d;
}

.forgot-password__strength.is-level-2 i.active {
  background: #e8a23d;
}

.forgot-password__strength.is-level-3 i.active {
  background: #0e9f5c;
}

.forgot-password__strength em {
  width: 52px;
  color: #5a6072;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  text-align: right;
  white-space: nowrap;
}

.forgot-password__security-tip {
  margin: 16px 0 0;
  color: #98a0af;
  font-size: 12.5px;
  line-height: 1.6;
  text-align: center;
}

@media (max-width: 980px) {
  .auth-page {
    grid-template-columns: 1fr;
  }

  .auth-page__story {
    padding: 32px 24px;
  }

  .auth-page__status {
    margin-top: 34px;
  }

  .auth-page__metrics,
  .auth-page__features,
  .auth-page__footer {
    display: none;
  }

  .auth-page__form-side {
    padding: 34px 16px 50px;
  }
}

@media (max-width: 520px) {
  .auth-page__story h1 {
    font-size: 32px;
  }

  .auth-panel {
    max-width: 100%;
  }

  .auth-panel__card {
    padding: 26px 20px;
  }

}
</style>
